/* Outer shell owns size + outer shadows. Masking .root would clip those
   shadows, so they stay here. */
.wrap {
    --ai-agent-icon-size: 16px;
    --ai-agent-icon-shadow: color-mix(in oklch, #5e4cff 26%, transparent);

    position: relative;
    display: inline-block;
    width: var(--ai-agent-icon-size);
    height: var(--ai-agent-icon-size);
    flex-shrink: 0;
    border-radius: 50%;
    box-shadow:
        0 1px 2px rgba(94, 76, 255, 0.16),
        0 0 0 1px var(--ai-agent-icon-shadow);
    transition: box-shadow 220ms ease;
}

.root {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 50%;
    isolation: isolate;
    /* Safari/WebKit: filter + mix-blend-mode layers ignore overflow+radius and
       paint a rectangular bitmap. A circular mask clips that layer correctly. */
    -webkit-mask-image: radial-gradient(closest-side, #000 99.5%, transparent);
    mask-image: radial-gradient(closest-side, #000 99.5%, transparent);
    background:
        radial-gradient(
            120% 120% at 28% 24%,
            rgba(255, 255, 255, 0.6) 0%,
            transparent 40%
        ),
        radial-gradient(115% 130% at 82% 24%, #c9b6ff 0%, transparent 52%),
        radial-gradient(130% 120% at 84% 84%, #5e4cff 0%, transparent 56%),
        radial-gradient(120% 130% at 16% 82%, #9836ff 0%, transparent 56%),
        conic-gradient(
            from 210deg at 50% 50%,
            #7a4cff,
            #b18cff,
            #5e4cff,
            #9836ff,
            #7a4cff
        );
    background-size: 180% 180%;
    background-position: 50% 50%;
    box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.5),
        inset 0 -4px 9px rgba(94, 76, 255, 0.18);
    transform: translateZ(0);
    transition:
        background-position 500ms ease,
        box-shadow 220ms ease,
        filter 240ms ease;
}

.muted {
    filter: grayscale(1) brightness(0.98);
}

.wrap:has(.muted) {
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.root::before,
.root::after {
    position: absolute;
    inset: -40%;
    content: '';
    opacity: 0;
    pointer-events: none;
    transition:
        opacity 320ms ease,
        transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.root::before {
    background:
        radial-gradient(46% 56% at 30% 28%, #ffffff 0%, transparent 60%),
        radial-gradient(48% 60% at 66% 34%, #b18cff 0%, transparent 64%),
        radial-gradient(46% 58% at 60% 70%, #9836ff 0%, transparent 64%),
        radial-gradient(44% 56% at 24% 66%, #5e4cff 0%, transparent 62%);
    filter: blur(6px);
    mix-blend-mode: screen;
}

.root::after {
    background: conic-gradient(
        from 90deg,
        transparent,
        rgba(255, 255, 255, 0.7),
        rgba(201, 182, 255, 0.5),
        transparent,
        rgba(152, 54, 255, 0.62),
        rgba(94, 76, 255, 0.6),
        transparent
    );
    filter: blur(8px);
    mix-blend-mode: soft-light;
}

.root:hover,
:global(button:hover) .root,
:global(a:hover) .root,
:global(.mantine-Group-root:hover) .root,
:global([role='menuitem']:hover) .root,
:global([data-combobox-selected='true']) .root {
    filter: none;
    box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.66),
        inset 0 -4px 10px rgba(94, 76, 255, 0.24);
    animation: ai-agent-icon-morph 6s ease infinite;
}

.wrap:hover,
:global(button:hover) .wrap,
:global(a:hover) .wrap,
:global(.mantine-Group-root:hover) .wrap,
:global([role='menuitem']:hover) .wrap,
:global([data-combobox-selected='true']) .wrap {
    box-shadow:
        0 2px 6px rgba(152, 54, 255, 0.22),
        0 0 0 1px color-mix(in oklch, #9836ff 36%, transparent);
}

.root:hover::before,
.root:hover::after,
:global(button:hover) .root::before,
:global(button:hover) .root::after,
:global(a:hover) .root::before,
:global(a:hover) .root::after,
:global(.mantine-Group-root:hover) .root::before,
:global(.mantine-Group-root:hover) .root::after,
:global([role='menuitem']:hover) .root::before,
:global([role='menuitem']:hover) .root::after,
:global([data-combobox-selected='true']) .root::before,
:global([data-combobox-selected='true']) .root::after {
    opacity: 1;
    animation: ai-agent-icon-drift 2.4s cubic-bezier(0.45, 0, 0.55, 1) 280ms
        infinite alternate;
}

.root:hover::after,
:global(button:hover) .root::after,
:global(a:hover) .root::after,
:global(.mantine-Group-root:hover) .root::after,
:global([role='menuitem']:hover) .root::after,
:global([data-combobox-selected='true']) .root::after {
    animation-duration: 2.6s;
    animation-direction: alternate-reverse;
}

.animated {
    filter: none;
    box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.66),
        inset 0 -4px 10px rgba(94, 76, 255, 0.24);
    animation: ai-agent-icon-morph 6s ease infinite;
}

.wrap:has(.animated) {
    box-shadow:
        0 2px 6px rgba(152, 54, 255, 0.22),
        0 0 0 1px color-mix(in oklch, #9836ff 36%, transparent);
}

.animated::before,
.animated::after {
    opacity: 1;
    animation: ai-agent-icon-drift 2.4s cubic-bezier(0.45, 0, 0.55, 1) 280ms
        infinite alternate;
}

.animated::after {
    animation-duration: 2.6s;
    animation-direction: alternate-reverse;
}

/* Calmer variant — slower morph, softer drift, gentler glow. */
.calm.animated {
    animation-duration: 8s;
    box-shadow:
        inset 0 0 0 1px rgba(255, 255, 255, 0.62),
        inset 0 -4px 10px rgba(94, 76, 255, 0.2);
}

.wrap:has(.calm.animated) {
    box-shadow:
        0 1px 4px rgba(152, 54, 255, 0.16),
        0 0 0 1px color-mix(in oklch, #9836ff 30%, transparent);
}

.calm.animated::before,
.calm.animated::after {
    opacity: 0.85;
    animation-name: ai-agent-icon-drift-calm;
    animation-duration: 3.6s;
}

.calm.animated::after {
    animation-duration: 3.9s;
}

@keyframes ai-agent-icon-drift-calm {
    from {
        transform: translate3d(-1%, -1%, 0) rotate(-2deg) scale(1);
    }

    to {
        transform: translate3d(3%, 2%, 0) rotate(7deg) scale(1.03);
    }
}

@keyframes ai-agent-icon-morph {
    0% {
        background-position: 43% 0%;
    }

    50% {
        background-position: 58% 100%;
    }

    100% {
        background-position: 43% 0%;
    }
}

@keyframes ai-agent-icon-drift {
    from {
        transform: translate3d(-2%, -1%, 0) rotate(-3deg) scale(1);
    }

    to {
        transform: translate3d(5%, 4%, 0) rotate(12deg) scale(1.05);
    }
}

@media (prefers-reduced-motion: reduce) {
    .root,
    .root::before,
    .root::after,
    .animated,
    .animated::before,
    .animated::after {
        animation: none !important;
        transition: opacity 120ms ease;
    }
}
